import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '60 minutes',
  session: '1 session',
  results: 'Results 12 months',
}

const relatedTreatments = [
  {
    name: 'Brow Lamination & Tint Cheshunt',
    slug: 'eyebrows-lamination-and-tint',
    description:
      'Try a non-invasive brow lamination and tint if you’re not ready for semi-permanent makeup.',
  },
  {
    name: 'Lash Lift & Tint Cheshunt',
    slug: 'lash-lift-and-tint',
    description:
      'Pair your new brows with beautifully curled, darkened lashes that last 6–8 weeks.',
  },
  {
    name: 'Dermaplaning Facial Cheshunt',
    slug: 'dermaplaning',
    description:
      'Smooth away peach fuzz and dead skin so your brows and makeup look ultra-polished.',
  },
]

const questions: Array<Question> = [
  {
    question: 'Does it hurt?',
    answer:
      'A little bit scratchy, but not painful. Ultra-fine nano-needles are less ' +
      'painful than the harder blades used in microblading, but nanoblading takes ' +
      'longer. Your Ada Aesthetics practitioner will apply an anaesthetic cream ' +
      'beforehand to make you more comfortable. Nanoblading causes less trauma' +
      'to the skin, so it has a shorter recovery time; expect swelling and redness ' +
      'to subside within ten days for both treatments.\t',
  },
  {
    question: 'Which one is right for me?',
    answer:
      'At Ada Aesthetics, your treatment will depend on your skin type and hair. ' +
      'Bear in mind that nanoblading lasts longer because the pigment has more ' +
      'depth, so it is particularly suitable for darker skins. It is also a good ' +
      'choice for oilier skins because sebum breaks down the pigment more quickly. ' +
      'Sometimes we combine both techniques.\t',
  },
]

const Microblading: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Microblading and Nanoblading in Cheshunt',
    'Professional semi-permanent eyebrow enhancement using microblading and nanoblading techniques. Expert brow tattooing in Cheshunt, Hertfordshire for natural-looking, defined eyebrows.',
    '200'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Microblading Cheshunt', url: 'https://adaaesthetics.uk/microblading' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Microblading & Nanoblading Cheshunt | Ada Aesthetics"
        description="Expert microblading and nanoblading in Cheshunt. Semi-permanent eyebrow enhancement for natural-looking, defined brows. Lasts 12 months. Book consultation: 07478705252"
        keywords="microblading Cheshunt, nanoblading Hertfordshire, semi-permanent brows, eyebrow tattooing Cheshunt, brow enhancement"
        url="https://adaaesthetics.uk/microblading"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax
        titleName="Microblading and Nanoblading"
        customClass="page_title"
      />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Perfectly Defined Brows That Last 12 Months with Microblading & Nanoblading in Cheshunt</h1>
              <p className="excerpt">
                Tired of spending precious minutes every morning filling in sparse, uneven brows? At Ada Aesthetics in Cheshunt, our expert microblading and nanoblading treatments create perfectly defined, symmetrical eyebrows that wake up flawless—every single day. No more smudged makeup or patchy brows. Just natural-looking, beautifully shaped eyebrows that last up to 12 months. Serving Cheshunt, Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
              <h2>What is Microblading?</h2>
              <p>
                Microblading is a semi-permanent cosmetic brow tattooing technique that makes your brows look
                fuller, more defined, and perfectly symmetrical. This gold-standard treatment frames your eyes and
                adds definition to your whole face. At Ada Aesthetics in Cheshunt, we use fine blades to create
                hair-like strokes that deliver pigment into the top layer of skin (the epidermis), creating natural-looking
                results that last up to 12 months.
              </p>

              <h2>What is Nanoblading?</h2>
              <p>
                Despite the name, nanoblading uses ultra-fine needles, not blades. This advanced technique is
                more precise than microblading, creating tiny strokes as thin as individual eyebrow hairs for
                incredibly natural results. Nanoblading is perfect for filling in thin eyebrows and defining arches,
                and typically lasts up to 3 years with proper touch-ups. At our Cheshunt clinic, we recommend
                nanoblading for clients with oilier skin or those seeking longer-lasting results.
              </p>

              <h2>Microblading vs Nanoblading: Which is Right for You?</h2>
              <p>
                At Ada Aesthetics in Cheshunt, your treatment will depend on your skin type and hair characteristics.
                Nanoblading lasts longer because the pigment has more depth, making it particularly suitable for
                darker skins and oilier complexions where sebum can break down pigment more quickly. Sometimes
                we combine both techniques for optimal results.
              </p>

              <h2>Your Microblading Treatment Journey</h2>
              <p>
                Your journey begins with a comprehensive consultation at our Cheshunt clinic, where we'll assess
                your skin type, discuss your brow goals, and determine whether microblading or nanoblading (or a
                combination) will deliver the best results for you.
              </p>
              <p>
                The treatment itself takes approximately 60 minutes. We apply anaesthetic cream beforehand to
                ensure your comfort. Nanoblading causes less trauma to the skin, so it has a shorter recovery time;
                expect swelling and redness to subside within ten days for both treatments.
              </p>

              <h2>Why Choose Ada Aesthetics for Microblading in Cheshunt?</h2>
              <ul>
                <li><strong>Expert practitioners</strong> trained in both microblading and nanoblading techniques</li>
                <li><strong>Natural-looking results</strong> - hair-like strokes that enhance your features</li>
                <li><strong>Long-lasting effects</strong> - up to 12 months for microblading, 3 years for nanoblading</li>
                <li><strong>Free consultations</strong> to determine the best technique for you</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
              </ul>

              <h2>Book Your Microblading Consultation in Cheshunt</h2>
              <p>
                Ready for perfectly defined brows that last? Join clients who trust us for expert semi-permanent
                eyebrow enhancement. Schedule your free consultation today - our experienced team will assess your
                brows and create a personalized treatment plan to achieve your perfect eyebrow aesthetic.
              </p>
              <p>
                <strong>Book today to secure your appointment</strong> - Call us at <strong>07478705252</strong> or reserve your consultation online.
                Your complimentary assessment (worth £50) includes brow analysis and custom design plan.
              </p>
            </div>
          </div>
          <ServiceTable treatment={treatment} questions={questions} />
          <RelatedTreatments treatments={relatedTreatments} />
        </div>
      </section>
    </Layout>
  )
}

export default Microblading

export const pageQuery = graphql`
  query Microblading {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
